// src/pages/Promotions.jsx
import React, { useState, useEffect } from "react";
import api, { API_BASE } from "../auth/api";
import { Tag, Plus, Trash2, Edit, Calendar, Clock, Check, X, Upload, AlertCircle } from "lucide-react";
import Swal from "sweetalert2";

const DAYS_OF_WEEK = [
  { value: "1", label: "Lunes", short: "Lun" },
  { value: "2", label: "Martes", short: "Mar" },
  { value: "3", label: "Miércoles", short: "Mié" },
  { value: "4", label: "Jueves", short: "Jue" },
  { value: "5", label: "Viernes", short: "Vie" },
  { value: "6", label: "Sábado", short: "Sáb" },
  { value: "0", label: "Domingo", short: "Dom" },
];

export default function PromotionsPage() {
  const [promotions, setPromotions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedPromo, setSelectedPromo] = useState(null);

  // Form states
  const [name, setName] = useState("");
  const [text, setText] = useState("");
  const [mediaPath, setMediaPath] = useState("");
  const [mediaMime, setMediaMime] = useState("");
  const [mediaSize, setMediaSize] = useState(0);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [activeDays, setActiveDays] = useState([]);
  const [priority, setPriority] = useState(0);
  const [isActive, setIsActive] = useState(true);

  // Load promotions
  const fetchPromotions = async () => {
    try {
      setLoading(true);
      const res = await api.get("/api/promotions");
      if (res.data?.ok) {
        setPromotions(res.data.rows || []);
      }
    } catch (err) {
      console.error("Error fetching promotions:", err);
      Swal.fire("Error", "No se pudieron cargar las promociones", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPromotions();
  }, []);

  const openAddModal = () => {
    setSelectedPromo(null);
    setName("");
    setText("");
    setMediaPath("");
    setMediaMime("");
    setMediaSize(0);
    setStartDate("");
    setEndDate("");
    setActiveDays([]);
    setPriority(0);
    setIsActive(true);
    setModalOpen(true);
  };

  const openEditModal = (promo) => {
    setSelectedPromo(promo);
    setName(promo.name || "");
    setText(promo.text || "");
    setMediaPath(promo.media_path || "");
    setMediaMime(promo.media_mime || "");
    setMediaSize(promo.media_size || 0);
    setStartDate(promo.start_date || "");
    setEndDate(promo.end_date || "");
    setActiveDays(promo.active_days ? promo.active_days.split(",") : []);
    setPriority(promo.priority || 0);
    setIsActive(promo.is_active === 1);
    setModalOpen(true);
  };

  // Handle file upload
  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);

    try {
      setUploading(true);
      const res = await api.post("/api/promotions/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (res.data?.ok) {
        setMediaPath(res.data.path);
        setMediaMime(file.type);
        setMediaSize(file.size);
        Swal.fire({
          icon: "success",
          title: "Imagen subida",
          toast: true,
          position: "top-end",
          showConfirmButton: false,
          timer: 2000,
        });
      }
    } catch (err) {
      console.error("Error uploading file:", err);
      Swal.fire("Error", "No se pudo subir la imagen", "error");
    } finally {
      setUploading(false);
    }
  };

  // Toggle active days checkboxes
  const handleDayToggle = (dayValue) => {
    setActiveDays((prev) =>
      prev.includes(dayValue)
        ? prev.filter((d) => d !== dayValue)
        : [...prev, dayValue]
    );
  };

  // Handle active status toggle directly from card
  const handleStatusToggle = async (promo) => {
    const newStatus = promo.is_active === 1 ? 0 : 1;
    try {
      const res = await api.put(`/api/promotions/${promo.id}`, {
        is_active: newStatus,
      });
      if (res.data?.ok) {
        setPromotions((prev) =>
          prev.map((p) => (p.id === promo.id ? { ...p, is_active: newStatus } : p))
        );
        Swal.fire({
          icon: "success",
          title: newStatus === 1 ? "Promoción activada" : "Promoción desactivada",
          toast: true,
          position: "top-end",
          showConfirmButton: false,
          timer: 1500,
        });
      }
    } catch (err) {
      console.error("Error toggling promotion status:", err);
      Swal.fire("Error", "No se pudo cambiar el estado", "error");
    }
  };

  // Delete promotion
  const handleDelete = async (id) => {
    const confirm = await Swal.fire({
      title: "¿Estás seguro?",
      text: "Esta acción eliminará la promoción y su imagen asociada de forma permanente.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Sí, eliminar",
      cancelButtonText: "Cancelar",
    });

    if (confirm.isConfirmed) {
      try {
        const res = await api.delete(`/api/promotions/${id}`);
        if (res.data?.ok) {
          Swal.fire("Eliminado", "La promoción ha sido eliminada.", "success");
          fetchPromotions();
        }
      } catch (err) {
        console.error("Error deleting promotion:", err);
        Swal.fire("Error", "No se pudo eliminar la promoción", "error");
      }
    }
  };

  // Save promotion (Create or Edit)
  const handleSave = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      Swal.fire("Validación", "El nombre es obligatorio", "warning");
      return;
    }

    const payload = {
      name,
      text,
      media_path: mediaPath || null,
      media_mime: mediaMime || null,
      media_size: mediaSize || null,
      start_date: startDate || null,
      end_date: endDate || null,
      active_days: activeDays.length > 0 ? activeDays.join(",") : null,
      priority: Number(priority),
      is_active: isActive ? 1 : 0,
    };

    try {
      if (selectedPromo) {
        // Edit mode
        const res = await api.put(`/api/promotions/${selectedPromo.id}`, payload);
        if (res.data?.ok) {
          Swal.fire("Guardado", "Promoción actualizada con éxito", "success");
          setModalOpen(false);
          fetchPromotions();
        }
      } else {
        // Add mode
        const res = await api.post("/api/promotions", payload);
        if (res.data?.ok) {
          Swal.fire("Creado", "Promoción creada con éxito", "success");
          setModalOpen(false);
          fetchPromotions();
        }
      }
    } catch (err) {
      console.error("Error saving promotion:", err);
      Swal.fire("Error", "No se pudo guardar la promoción", "error");
    }
  };

  const getMediaUrl = (path) => {
    if (!path) return null;
    return path.startsWith("http") ? path : `${API_BASE}${path}`;
  };

  const renderActiveDays = (daysStr) => {
    if (!daysStr) return "Todos los días";
    const days = daysStr.split(",");
    return DAYS_OF_WEEK.filter((d) => days.includes(d.value))
      .map((d) => d.short)
      .join(", ");
  };

  return (
    <div className="p-6 bg-neutral-900 min-h-screen text-neutral-100 font-sans">
      {/* Banner / Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8 bg-neutral-950 p-6 rounded-2xl border border-neutral-800 shadow-xl relative overflow-hidden">
        <div className="absolute right-0 top-0 w-64 h-64 bg-pink-500/5 rounded-full blur-3xl pointer-events-none"></div>
        <div>
          <div className="flex items-center gap-3">
            <div className="p-3 bg-pink-500/10 text-pink-500 rounded-xl border border-pink-500/20">
              <Tag className="h-6 w-6" />
            </div>
            <div>
              <h1 className="text-2xl font-bold tracking-tight">Módulo de Promociones</h1>
              <p className="text-sm text-neutral-400 mt-0.5">
                Programa imágenes y textos automáticos para responder consultas de WhatsApp según la fecha, vigencia o día de la semana.
              </p>
            </div>
          </div>
        </div>
        <button
          id="btn-new-promotion"
          onClick={openAddModal}
          className="flex items-center gap-2 px-4 py-2.5 bg-pink-600 hover:bg-pink-500 active:bg-pink-700 text-white rounded-xl font-medium shadow-lg hover:shadow-pink-500/15 transition-all duration-200"
        >
          <Plus size={18} />
          Nueva Promoción
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center items-center py-20">
          <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-pink-500"></div>
        </div>
      ) : promotions.length === 0 ? (
        <div className="text-center py-16 bg-neutral-950 rounded-2xl border border-neutral-800 p-8 shadow-md">
          <div className="mx-auto w-12 h-12 flex items-center justify-center rounded-full bg-neutral-900 border border-neutral-800 text-neutral-500 mb-4">
            <Tag size={24} />
          </div>
          <h3 className="text-lg font-semibold text-neutral-300">No hay promociones registradas</h3>
          <p className="text-sm text-neutral-500 max-w-sm mx-auto mt-1">
            Crea tu primera promoción programada para que el chatbot de WhatsApp la comparta automáticamente.
          </p>
          <button
            onClick={openAddModal}
            className="mt-4 px-4 py-2 bg-neutral-800 hover:bg-neutral-700 rounded-xl text-xs font-medium border border-neutral-700 transition"
          >
            Comenzar ahora
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {promotions.map((promo) => {
            const mediaUrl = getMediaUrl(promo.media_path);
            const active = promo.is_active === 1;
            return (
              <div
                key={promo.id}
                className={`bg-neutral-950 rounded-2xl border ${
                  active ? "border-neutral-800 hover:border-pink-500/30" : "border-neutral-900 opacity-65"
                } overflow-hidden shadow-lg transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl flex flex-col`}
              >
                {/* Media Preview Section */}
                <div className="h-44 w-full relative bg-neutral-900 flex items-center justify-center overflow-hidden border-b border-neutral-900">
                  {mediaUrl ? (
                    <img src={mediaUrl} alt={promo.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="flex flex-col items-center gap-2 text-neutral-600">
                      <Tag size={40} className="stroke-1" />
                      <span className="text-[10px] uppercase font-bold tracking-wider">Solo texto</span>
                    </div>
                  )}
                  {/* Badge Priority */}
                  <span className="absolute top-3 left-3 px-2 py-1 text-[10px] font-bold bg-neutral-950/80 backdrop-blur-md text-pink-400 rounded-lg border border-pink-500/20">
                    Prioridad: {promo.priority}
                  </span>
                  {/* Status Toggle Switch */}
                  <button
                    onClick={() => handleStatusToggle(promo)}
                    className={`absolute top-3 right-3 flex items-center justify-center p-1.5 rounded-lg border backdrop-blur-md transition-all ${
                      active
                        ? "bg-emerald-500/10 border-emerald-500/35 text-emerald-400"
                        : "bg-neutral-950/60 border-neutral-800 text-neutral-500"
                    }`}
                    title={active ? "Desactivar" : "Activar"}
                  >
                    {active ? <Check size={14} /> : <X size={14} />}
                  </button>
                </div>

                {/* Content Section */}
                <div className="p-5 flex-1 flex flex-col">
                  <div className="flex justify-between items-start gap-2 mb-2">
                    <h3 className="font-bold text-lg text-neutral-100 truncate">{promo.name}</h3>
                  </div>

                  <p className="text-neutral-400 text-xs line-clamp-3 mb-4 flex-1">
                    {promo.text || <em className="text-neutral-600">Sin descripción textual</em>}
                  </p>

                  <div className="space-y-2 border-t border-neutral-900 pt-4 text-xs text-neutral-400">
                    {/* Active Days */}
                    <div className="flex items-center gap-2">
                      <Clock size={14} className="text-neutral-500" />
                      <span>{renderActiveDays(promo.active_days)}</span>
                    </div>
                    {/* Date Range */}
                    <div className="flex items-center gap-2">
                      <Calendar size={14} className="text-neutral-500" />
                      <span>
                        {promo.start_date || promo.end_date ? (
                          <>
                            {promo.start_date ? promo.start_date : "Siempre"} al{" "}
                            {promo.end_date ? promo.end_date : "Siempre"}
                          </>
                        ) : (
                          "Siempre vigente"
                        )}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Actions Footer */}
                <div className="px-5 py-3.5 bg-neutral-950 border-t border-neutral-900 flex justify-end gap-2.5">
                  <button
                    onClick={() => openEditModal(promo)}
                    className="p-2 hover:bg-neutral-900 text-neutral-400 hover:text-white rounded-xl transition-all border border-transparent hover:border-neutral-800"
                    title="Editar"
                  >
                    <Edit size={16} />
                  </button>
                  <button
                    onClick={() => handleDelete(promo.id)}
                    className="p-2 hover:bg-red-500/10 text-neutral-400 hover:text-red-400 rounded-xl transition-all border border-transparent hover:border-red-500/25"
                    title="Eliminar"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Sleek Dialog Modal for Add / Edit */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 overflow-y-auto">
          <div className="bg-neutral-950 border border-neutral-800 w-full max-w-2xl rounded-2xl shadow-2xl overflow-hidden my-8 animate-in fade-in zoom-in-95 duration-200">
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-neutral-800 bg-neutral-950 flex justify-between items-center">
              <h2 className="text-lg font-bold">
                {selectedPromo ? "Editar Promoción" : "Crear Nueva Promoción"}
              </h2>
              <button
                onClick={() => setModalOpen(false)}
                className="text-neutral-400 hover:text-white p-1 rounded-lg hover:bg-neutral-900 transition"
              >
                <X size={20} />
              </button>
            </div>

            {/* Modal Body / Form */}
            <form onSubmit={handleSave} className="p-6 space-y-5 max-h-[70vh] overflow-y-auto custom-scrollbar">
              {/* Promotion Name */}
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-neutral-400 mb-1.5">
                  Nombre de la Promoción *
                </label>
                <input
                  id="promo-form-name"
                  type="text"
                  required
                  placeholder="Ej. Promoción de Lunes Loco o Día de la Madre"
                  className="w-full bg-neutral-900 border border-neutral-800 rounded-xl px-4 py-2.5 text-sm text-neutral-100 placeholder-neutral-500 focus:outline-none focus:border-pink-500 transition"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </div>

              {/* Message text */}
              <div>
                <div className="flex justify-between items-center mb-1.5">
                  <label className="block text-xs font-semibold uppercase tracking-wider text-neutral-400">
                    Mensaje de WhatsApp (Caption)
                  </label>
                  <span className="text-[10px] text-neutral-500">
                    {text.length} caracteres
                  </span>
                </div>
                <textarea
                  id="promo-form-text"
                  rows={4}
                  placeholder="Escribe el texto de la promoción que se enviará al cliente. Puedes usar {{nombre}} para personalizar."
                  className="w-full bg-neutral-900 border border-neutral-800 rounded-xl px-4 py-2.5 text-sm text-neutral-100 placeholder-neutral-500 focus:outline-none focus:border-pink-500 transition resize-none"
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                />
              </div>

              {/* Image Upload Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider text-neutral-400 mb-1.5">
                    Imagen de la Promoción
                  </label>
                  <div className="flex items-center justify-center w-full">
                    <label className="flex flex-col items-center justify-center w-full h-36 border-2 border-neutral-800 border-dashed rounded-xl cursor-pointer bg-neutral-900/40 hover:bg-neutral-900 hover:border-pink-500/50 transition">
                      <div className="flex flex-col items-center justify-center pt-5 pb-6 px-4 text-center">
                        <Upload className="h-8 w-8 text-neutral-500 mb-2" />
                        <p className="text-xs text-neutral-400">
                          {uploading ? "Subiendo..." : "Haz clic para subir una imagen"}
                        </p>
                        <p className="text-[10px] text-neutral-500 mt-1">PNG, JPG, JPEG o WEBP</p>
                      </div>
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={handleFileUpload}
                        disabled={uploading}
                      />
                    </label>
                  </div>
                </div>

                {/* Upload Preview */}
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider text-neutral-400 mb-1.5">
                    Vista previa de imagen
                  </label>
                  <div className="h-36 bg-neutral-900 border border-neutral-800 rounded-xl flex items-center justify-center overflow-hidden relative">
                    {mediaPath ? (
                      <>
                        <img
                          src={getMediaUrl(mediaPath)}
                          alt="Previsualización"
                          className="w-full h-full object-cover"
                        />
                        <button
                          type="button"
                          onClick={() => {
                            setMediaPath("");
                            setMediaMime("");
                            setMediaSize(0);
                          }}
                          className="absolute top-2 right-2 p-1 bg-red-500/80 hover:bg-red-600 rounded-lg text-white transition"
                          title="Eliminar imagen"
                        >
                          <X size={14} />
                        </button>
                      </>
                    ) : (
                      <span className="text-xs text-neutral-600">Sin imagen seleccionada</span>
                    )}
                  </div>
                </div>
              </div>

              {/* Date Ranges */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider text-neutral-400 mb-1.5">
                    Fecha de Inicio (Vigencia)
                  </label>
                  <div className="relative">
                    <input
                      id="promo-form-start"
                      type="date"
                      className="w-full bg-neutral-900 border border-neutral-800 rounded-xl px-4 py-2.5 text-sm text-neutral-100 focus:outline-none focus:border-pink-500 transition"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider text-neutral-400 mb-1.5">
                    Fecha de Fin (Vigencia)
                  </label>
                  <div className="relative">
                    <input
                      id="promo-form-end"
                      type="date"
                      className="w-full bg-neutral-900 border border-neutral-800 rounded-xl px-4 py-2.5 text-sm text-neutral-100 focus:outline-none focus:border-pink-500 transition"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                    />
                  </div>
                </div>
              </div>

              {/* Day of Week Selector */}
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-neutral-400 mb-2">
                  Días de la semana que se realiza la promoción
                </label>
                <div className="flex flex-wrap gap-2.5">
                  {DAYS_OF_WEEK.map((day) => {
                    const selected = activeDays.includes(day.value);
                    return (
                      <button
                        type="button"
                        key={day.value}
                        onClick={() => handleDayToggle(day.value)}
                        className={`px-3 py-1.5 text-xs font-medium rounded-xl border transition-all ${
                          selected
                            ? "bg-pink-600/10 border-pink-500/40 text-pink-400"
                            : "bg-neutral-900 border-neutral-800 text-neutral-400 hover:text-white"
                        }`}
                      >
                        {day.label}
                      </button>
                    );
                  })}
                </div>
                <p className="text-[10px] text-neutral-500 mt-1.5">
                  * Si no seleccionas ningún día, estará activa de forma permanente todos los días.
                </p>
              </div>

              {/* Priority and Active Toggle */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5 pt-2">
                <div>
                  <div className="flex justify-between items-center mb-1.5">
                    <label className="block text-xs font-semibold uppercase tracking-wider text-neutral-400">
                      Prioridad
                    </label>
                    <span className="text-[10px] text-neutral-500">Valor: {priority}</span>
                  </div>
                  <input
                    id="promo-form-priority"
                    type="range"
                    min="0"
                    max="100"
                    className="w-full accent-pink-600 bg-neutral-900 cursor-pointer h-2 rounded-lg appearance-none"
                    value={priority}
                    onChange={(e) => setPriority(Number(e.target.value))}
                  />
                  <span className="text-[10px] text-neutral-500 flex items-center gap-1 mt-1">
                    <AlertCircle size={10} /> Las de mayor prioridad tienen preferencia si coinciden en fecha o día.
                  </span>
                </div>

                <div className="flex items-center justify-between p-4 bg-neutral-900/50 rounded-xl border border-neutral-800/80">
                  <div>
                    <label className="block text-xs font-bold text-neutral-300">
                      ¿Promoción Habilitada?
                    </label>
                    <span className="text-[10px] text-neutral-500">Habilita o pausa el envío automático.</span>
                  </div>
                  <button
                    id="promo-form-active"
                    type="button"
                    onClick={() => setIsActive(!isActive)}
                    className={`w-12 h-6 flex items-center rounded-full p-1 transition-all duration-300 ${
                      isActive ? "bg-emerald-500" : "bg-neutral-800"
                    }`}
                  >
                    <div
                      className={`bg-white w-4 h-4 rounded-full shadow-md transform transition-transform duration-300 ${
                        isActive ? "translate-x-6" : "translate-x-0"
                      }`}
                    ></div>
                  </button>
                </div>
              </div>

              {/* Modal Footer */}
              <div className="pt-4 border-t border-neutral-800 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setModalOpen(false)}
                  className="px-4 py-2 bg-neutral-900 hover:bg-neutral-800 text-neutral-300 border border-neutral-800 rounded-xl text-sm transition"
                >
                  Cancelar
                </button>
                <button
                  id="btn-save-promotion"
                  type="submit"
                  disabled={uploading}
                  className="px-5 py-2 bg-pink-600 hover:bg-pink-500 text-white rounded-xl text-sm font-semibold shadow-lg hover:shadow-pink-500/10 transition disabled:opacity-50"
                >
                  {selectedPromo ? "Guardar Cambios" : "Crear Promoción"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
